/**
 * Pure auto-dismiss timing for {@link ToastCard}, factored out so the
 * pause/resume accounting can be tested without a DOM or fake timers.
 *
 * A toast pauses while it is focused *or* hovered and resumes only once
 * neither holds. The earlier implementation tracked a single boolean and
 * restarted the timer from the full timeout on every resume, so a toast the
 * user kept brushing past never made progress toward dismissal. Here the
 * remaining budget is debited by however long each active run lasted, so the
 * toast always finishes its original lifetime once interaction stops.
 *
 * Time is passed in (a monotonic `now` in ms) rather than read from a clock so
 * the reducer is deterministic under test.
 */

export interface ToastTimerState {
  /** Total auto-dismiss budget. `<= 0` means persistent (never auto-dismiss). */
  readonly totalMs: number;
  /** Budget left to spend once the timer is next running, in ms. */
  readonly remainingMs: number;
  /** `now` at which the currently-running timer started, or null when paused. */
  readonly runStartedAt: number | null;
  readonly focused: boolean;
  readonly hovered: boolean;
}

export function createToastTimerState(totalMs: number, now: number): ToastTimerState {
  // A persistent toast carries no budget; it never schedules a run.
  if (totalMs <= 0) {
    return { totalMs, remainingMs: 0, runStartedAt: null, focused: false, hovered: false };
  }
  return { totalMs, remainingMs: totalMs, runStartedAt: now, focused: false, hovered: false };
}

/** A toast is held open while it is focused or hovered. */
export function isPaused(state: Pick<ToastTimerState, "focused" | "hovered">): boolean {
  return state.focused || state.hovered;
}

/** Budget remaining if a run that started at `runStartedAt` is settled at `now`. */
function debit(state: ToastTimerState, now: number): number {
  if (state.runStartedAt === null) return state.remainingMs;
  const elapsed = Math.max(0, now - state.runStartedAt);
  return Math.max(0, state.remainingMs - elapsed);
}

export type ToastTimerEvent =
  | { type: "focus" }
  | { type: "blur" }
  | { type: "mouseenter" }
  | { type: "mouseleave" };

/**
 * Folds a focus/hover transition into the timer state. Persistent toasts and
 * already-elapsed toasts are inert. Crossing into paused freezes the remaining
 * budget; crossing back to active restarts the run from that frozen budget.
 */
export function reduceToastTimer(
  state: ToastTimerState,
  event: ToastTimerEvent,
  now: number,
): ToastTimerState {
  if (state.totalMs <= 0) return state;

  const focused = event.type === "focus" ? true : event.type === "blur" ? false : state.focused;
  const hovered =
    event.type === "mouseenter" ? true : event.type === "mouseleave" ? false : state.hovered;

  // Nothing actually moved — e.g. focus bubbling between the toast's own
  // buttons fires blur+focus on the container without changing either flag.
  // Return the same reference so React's setState bailout skips the re-render
  // (and the effect's timer re-arm) instead of churning the dismiss countdown.
  if (focused === state.focused && hovered === state.hovered) return state;

  const wasPaused = isPaused(state);
  const nowPaused = isPaused({ focused, hovered });
  if (wasPaused === nowPaused) {
    // A flag flipped but the active/paused verdict held (e.g. blur while still
    // hovered): record the new flags but keep the run untouched so we don't
    // reset or double-debit the budget.
    return { ...state, focused, hovered };
  }

  if (nowPaused) {
    // Active -> paused: bank the remaining budget and stop the run.
    return { ...state, focused, hovered, remainingMs: debit(state, now), runStartedAt: null };
  }
  // Paused -> active: resume with whatever budget is left.
  return { ...state, focused, hovered, runStartedAt: now };
}

/**
 * Delay (ms) to arm a `setTimeout` with for the current run, or null when no
 * timer should be scheduled (paused, persistent, or already elapsed). The
 * value is clamped to `>= 0` so a run that should already have fired arms with
 * a zero delay rather than a negative one.
 */
export function timerDelayMs(state: ToastTimerState, now: number): number | null {
  if (state.totalMs <= 0) return null;
  if (isPaused(state)) return null;
  if (state.runStartedAt === null) return null;
  return debit(state, now);
}
